import React from 'react';
import { Table, Input, InputNumber, Select, AutoComplete, Button } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

// normalized view of rows for comparison / persisting: drops rows without a
// field name and strips empty cells
const stripEmpty = (rows) =>
    (rows || [])
        .filter((r) => r.field)
        .map((r) => {
            const clean = {};
            Object.keys(r).forEach((k) => {
                if (k !== 'key' && r[k] !== undefined && r[k] !== null && r[k] !== '') {
                    clean[k] = r[k];
                }
            });
            return clean;
        });

/**
 * Generic editable table.
 * columns: [{key,title,type:'field'|'text'|'number'|'select',options}]
 *  - 'field' renders an AutoComplete fed with the form's field names
 *
 * Rows live in local state so freshly added (still empty) rows survive the
 * round-trip through the config object; external config changes (e.g. via
 * code view) re-sync the local state.
 */
const FieldsEditor = ({ title, rows: propRows, columns, formFields, onChange, addLabel }) => {
    const [rows, setRows] = React.useState(propRows || []);

    React.useEffect(() => {
        if (JSON.stringify(stripEmpty(propRows)) !== JSON.stringify(stripEmpty(rows))) {
            setRows(propRows || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propRows]);

    const apply = (next, propagate) => {
        setRows(next);
        if (propagate) {
            onChange(next);
        }
    };

    const update = (i, key, value) => {
        apply(rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)), true);
    };
    const remove = (i) => apply(rows.filter((_, idx) => idx !== i), true);
    // a brand-new row has no field name yet, so it would be stripped on save -
    // keep it local only until something is entered
    const add = () => apply([...rows, {}], false);

    const cols = [
        ...columns.map((c) => ({
            title: c.title,
            dataIndex: c.key,
            key: c.key,
            width: c.width,
            render: (v, r, i) => {
                if (c.type === 'number') {
                    return <InputNumber value={v} onChange={(val) => update(i, c.key, val)} style={{ width: '100%' }} />;
                }
                if (c.type === 'select') {
                    return (
                        <Select
                            value={v}
                            allowClear
                            options={(c.options || []).map((o) => ({ value: o }))}
                            onChange={(val) => update(i, c.key, val)}
                            style={{ width: '100%' }}
                        />
                    );
                }
                if (c.type === 'field') {
                    // formFields entries: strings (Remedy) or {name, description}
                    // objects from connector metadata (e.g. IS field ids)
                    const fieldOptions = (formFields || []).map((f) => {
                        const nf = typeof f === 'string' ? { name: f } : (f || { name: '' });
                        return {
                            value: nf.name,
                            label: nf.description ? `${nf.name} — ${nf.description}` : nf.name
                        };
                    });
                    return (
                        <AutoComplete
                            value={v}
                            options={fieldOptions}
                            filterOption={(input, o) => `${o.value} ${o.label}`.toLowerCase().includes(input.toLowerCase())}
                            onChange={(val) => update(i, c.key, val)}
                            style={{ width: '100%' }}
                        />
                    );
                }
                return <Input value={v} onChange={(e) => update(i, c.key, e.target.value)} />;
            }
        })),
        {
            title: '',
            key: '__del',
            width: 45,
            render: (_, r, i) => (
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(i)} />
            )
        }
    ];

    return (
        <>
            <Table
                size="small"
                title={title ? () => title : undefined}
                pagination={false}
                dataSource={rows.map((r, i) => ({ ...r, key: i }))}
                columns={cols}
            />
            <Button icon={<PlusOutlined />} type="dashed" size="small" style={{ margin: '8px 0' }} onClick={add}>
                {addLabel || 'add field'}
            </Button>
        </>
    );
};

export default FieldsEditor;
